import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { logError } from '../utils/logger';
import {
  CheckCircle, Crown, Zap, TrendingUp, Shield, Star, ArrowRight, Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FoundingCheckout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [loading, setLoading] = useState(false);
  const [foundingRemaining, setFoundingRemaining] = useState(null);
  const [loadingCount, setLoadingCount] = useState(true);

  useEffect(() => {
    fetchFoundingCount();
  }, []);

  const fetchFoundingCount = async () => {
    try {
      const response = await axios.get(`${API}/waitlist/founding-count`);
      setFoundingRemaining(response.data.remaining);
    } catch (error) {
      logError(error, { context: 'FoundingCheckout.fetchFoundingCount' });
    } finally {
      setLoadingCount(false);
    }
  };

  const handleClaimSpot = async () => {
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const origin = window.location.origin;
      const response = await axios.post(`${API}/waitlist/founding-checkout`, {
        email,
        origin_url: origin
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;

    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to create checkout';
      
      // Better error message with action
      if (errorMsg.includes('not found on waitlist')) {
        toast.error(
          <div>
            <p className="font-bold">Not on waitlist yet!</p>
            <p className="text-sm mt-1">Join the waitlist first to claim your founding spot.</p>
          </div>,
          { duration: 5000 }
        );
        // Redirect to waitlist after 2 seconds
        setTimeout(() => navigate('/waitlist'), 2000);
      } else {
        toast.error(errorMsg);
      }
      
      setLoading(false);
    }
  };

  if (loadingCount) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  if (foundingRemaining === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardHeader className="text-center">
            <Crown className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <CardTitle className="text-2xl">Founding Member Spots Full!</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-600">
              All 100 founding member spots have been claimed! But don't worry - you can still get early access.
            </p>
            <Button 
              onClick={() => navigate('/waitlist')}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              Join Waitlist for Early Access
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <Badge className="bg-amber-500 text-white mb-4 px-4 py-2 text-sm">
            🔥 FOUNDING MEMBER OFFER
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Become a Founding Member
          </h1>
          <p className="text-xl text-slate-300">
            Lock in <span className="text-amber-400 font-bold">$79/year</span> forever
          </p>
        </div>

        {/* Main Card */}
        <Card className="bg-white">
          <CardContent className="p-8">
            {/* Urgency Banner */}
            <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4 mb-6 text-center">
              <p className="text-amber-900 font-bold text-lg">
                ⚡ Only {foundingRemaining} of 100 Spots Remaining!
              </p>
              <p className="text-amber-700 text-sm mt-1">
                This offer will never be available again
              </p>
            </div>

            {/* What You Get */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Crown className="w-6 h-6 text-amber-500" />
                What's Included
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { icon: Zap, text: "AI-powered categorization (95% accuracy)" },
                  { icon: TrendingUp, text: "Real-time tax estimates" },
                  { icon: CheckCircle, text: "Unlimited transactions & receipts" },
                  { icon: Shield, text: "Bank-level security & encryption" },
                  { icon: Star, text: "4 AI tax advisors" },
                  { icon: CheckCircle, text: "CPA-grade tax package exports" },
                  { icon: Crown, text: "Founding Member badge" },
                  { icon: Star, text: "Priority support" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <item.icon className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Price Comparison */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 mb-8">
              <h3 className="font-bold text-lg mb-4">💰 You're Saving $149/Year</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Standard Price:</span>
                  <span className="line-through text-gray-500">$228/year</span>
                </div>
                <div className="flex justify-between font-bold text-emerald-700">
                  <span>Founding Member Price:</span>
                  <span>$79/year</span>
                </div>
                <div className="border-t border-emerald-200 pt-2 mt-2 flex justify-between font-bold text-lg">
                  <span>Your Savings:</span>
                  <span className="text-emerald-600">$149/year</span>
                </div>
              </div>
            </div>

            {/* Guarantee */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-center">
              <p className="text-blue-900 font-medium">
                ✅ 30-Day Money Back Guarantee
              </p>
              <p className="text-blue-700 text-sm mt-1">
                Not satisfied? Get a full refund, no questions asked.
              </p>
            </div>

            {/* Email Input (if not from waitlist) */}
            {!searchParams.get('email') && (
              <div className="mb-6">
                <Label className="text-sm font-medium text-gray-700 mb-2 block">
                  Email Address
                </Label>
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="text-base"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  We'll send your receipt and access details here
                </p>
              </div>
            )}

            {/* CTA Button */}
            <Button
              onClick={handleClaimSpot}
              disabled={loading || foundingRemaining === 0}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-6 text-lg font-bold shadow-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Creating Checkout...
                </>
              ) : (
                <>
                  <Crown className="w-5 h-5 mr-2" />
                  Claim My Founding Spot - $79/year
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>

            <p className="text-xs text-center text-gray-500 mt-4">
              Secure payment powered by Stripe • Cancel anytime
            </p>
          </CardContent>
        </Card>

        {/* Trust Signals */}
        <div className="text-center mt-8">
          <p className="text-slate-400 text-sm">
            Questions? Email us at <a href="mailto:support@solelytax.ai" className="text-emerald-400 hover:underline">support@solelytax.ai</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default FoundingCheckout;
